import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { OnboardingStatus, type OnboardingSubmissionDto } from '@ciimar/shared';
import { api } from '../api/client';
import { useT } from '../lib/i18n';
import { STATUS_CLASS, useStatusLabels, useFormatDate } from './status';
import { PersonCell } from '../components/PersonCell';

export function MyTeam() {
  const t = useT();
  const statusLabels = useStatusLabels();
  const formatDate = useFormatDate();
  const [rows, setRows] = useState<OnboardingSubmissionDto[]>([]);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await api.listMyTeam({ status: status || undefined }));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section>
      <div className="page-head">
        <div className="page-head-text">
          <h2>{t('teams.myteam.title')}</h2>
          <p className="page-sub">{t('teams.myteam.subtitle')}</p>
        </div>
        <div className="spacer" />
        <Link to="/new">
          <button type="button">{t('teams.myteam.newRecord')}</button>
        </Link>
      </div>

      {error && <div className="alert error">{error}</div>}

      <div className="erp-panel">
        <div className="erp-toolbar">
          <span className="page-sub">
            {loading
              ? t('common.loading')
              : rows.length === 1
                ? t('teams.myteam.recordOne')
                : t('teams.myteam.records', { n: rows.length })}
          </span>
          <select
            className="erp-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label={t('teams.myteam.filterByStatus')}
          >
            <option value="">{t('teams.myteam.allStatuses')}</option>
            {Object.values(OnboardingStatus).map((s) => (
              <option key={s} value={s}>
                {statusLabels[s]}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="panel-empty">{t('common.loading')}</div>
        ) : rows.length === 0 ? (
          <div className="panel-empty is-empty">{t('teams.myteam.empty')}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="grid">
              <thead>
                <tr>
                  <th>{t('teams.myteam.colName')}</th>
                  <th>{t('teams.myteam.colEmail')}</th>
                  <th>{t('teams.myteam.colStatus')}</th>
                  <th>{t('teams.myteam.colReceived')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <PersonCell name={r.fullName}>
                        <Link to={`/submissions/${r.id}`}>{r.fullName}</Link>
                      </PersonCell>
                    </td>
                    <td>{r.email}</td>
                    <td>
                      <span className={STATUS_CLASS[r.status]}>{statusLabels[r.status]}</span>
                    </td>
                    <td>{formatDate(r.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
